import type { Tagged } from "@-/com.npmjs.type-fest";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useGraphemePinyinAudio } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.audio";

type PinyinAudioCard = {
  pinyinList: readonly { toneNumbersPinyin: string }[];
} & ({ grapheme: string } | { front: string });

export type PinyinSoundButtonGate = Tagged<
  {
    /** Plays the selected pinyin (or the first study pinyin if none is selected). */
    play: () => Promise<void>;
    /** Plays every study pinyin in order. */
    playSequence: () => Promise<void>;
  },
  "PinyinSoundButtonGate"
>;

/**
 * Whether a card's pinyin has bundled audio to play. `null` is the designed hidden state: a card
 * whose (selected) reading has no recording shows no speaker icon. The gate carries the press-only
 * playback callbacks, which are stable per card + selected reading (the mute override is sampled
 * at play time), so the parent re-renders only when the card or selection changes.
 */
export function usePinyinSoundButtonGate(
  card: PinyinAudioCard,
  selectedPinyin?: string | null,
): PinyinSoundButtonGate | null {
  const [isMutedOverride] = useContextSelectors(NavContext, (c) => c.isMutedOverride);
  const { hasAudio, play, playSequence } = useGraphemePinyinAudio(card, selectedPinyin, {
    isMutedOverride,
  });
  return hasAudio ? createGate<PinyinSoundButtonGate>({ play, playSequence }) : null;
}
